"use client";

import styles from "@/styles/modules/components/gallery.module.scss";
import Image from "next/image";
import { useState } from "react";
import { GallerySliderItem } from "@/types/wordpress-parts";
import Video from "./Video";

interface GalleryProps {
  gallery: GallerySliderItem[];
}

export default function Gallery({ gallery }: GalleryProps) {
  const safeGallery = gallery ?? [];

  const [translate, setTranslate] = useState<number>(0);
  const [counter, setCounter] = useState<number>(0);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const pauseVideos = () => {
    document.querySelectorAll("video").forEach((vid) => vid.pause());
  };

  const handleLeft = () => {
    if (counter > 0) {
      setTranslate((prevValue) => prevValue - 100);
      setCounter((prevValue) => prevValue - 1);
      pauseVideos();
    }
  };

  const handleRight = () => {
    if (counter < safeGallery.length - 1) {
      setTranslate((prevValue) => prevValue + 100);
      setCounter((prevValue) => prevValue + 1);
      pauseVideos();
    }
  };

  const getImage = (media: any) => media?.image?.node;

  return (
    <section className={styles.gallery}>
      {safeGallery.length === 1 &&
        safeGallery[0]?.imageOrVideo?.[0] === "Image" &&
        getImage(safeGallery[0]) && (
          <div className={styles.image} data-aos="fade-in" data-aos-delay="200">
            {(() => {
              const img = getImage(safeGallery[0]);
              if (!img) return null;

              return (
                <Image
                  alt={img.altText || "Imperial Riding School"}
                  src={img.link}
                  width={img.mediaDetails?.width ?? 0}
                  height={img.mediaDetails?.height ?? 0}
                />
              );
            })()}
          </div>
        )}

      {safeGallery.length === 1 &&
        safeGallery[0]?.imageOrVideo?.[0] === "Video" && (
          <div className={styles.image} data-aos="fade-in" data-aos-delay="200">
            <Video video={safeGallery[0]?.video as string} />
          </div>
        )}

      {safeGallery.length !== 1 && (
        <>
          <div
            className={styles.images}
            style={{ transform: `translateX(-${translate}%)` }}
            data-aos="fade-in"
          >
            {safeGallery.map((media, i) => {
              const img = getImage(media);

              return media?.imageOrVideo?.[0] === "Image" && img ? (
                <Image
                  key={i}
                  alt={img.altText || "Imperial Riding School"}
                  src={img.link}
                  width={img.mediaDetails?.width ?? 0}
                  height={img.mediaDetails?.height ?? 0}
                  data-aos="fade-in"
                  data-aos-delay="200"
                />
              ) : media?.video ? (
                <Video
                  key={i}
                  video={media.video}
                  data-aos="fade-in"
                  data-aos-delay="200"
                />
              ) : null;
            })}
          </div>

          <div className={styles.icons} data-aos="fade-in" data-aos-delay="300">
            <span className={styles.fullscreenIcon}>
              <Image
                alt="Fullscreen icon"
                src="/icons/fullscreen-icon.svg"
                width={20}
                height={20}
                onClick={() => {
                  setIsFullscreen(true);
                  pauseVideos();
                }}
              />
            </span>

            <span
              className={`${styles.arrow} ${styles.arrowLeft} ${
                counter === 0 ? styles.disabled : ""
              }`}
            >
              <Image
                alt="Arrow left icon"
                src="/icons/arrow-left-icon.svg"
                width={15}
                height={31}
                onClick={handleLeft}
              />
            </span>

            <span
              className={`${styles.arrow} ${styles.arrowRight} ${
                counter === safeGallery.length - 1 ? styles.disabled : ""
              }`}
            >
              <Image
                alt="Arrow right icon"
                src="/icons/arrow-right-icon.svg"
                width={15}
                height={31}
                onClick={handleRight}
              />
            </span>

            <span className={styles.counter}>
              {counter + 1} / {safeGallery.length}
            </span>
          </div>

          <div
            className={`${styles.fullscreen} ${
              isFullscreen ? styles.active : ""
            }`}
          >
            <div className={styles.icons}>
              <span className={styles.close}>
                <Image
                  alt="Close icon"
                  src="/icons/close-icon.svg"
                  width={41}
                  height={31}
                  onClick={() => {
                    setIsFullscreen(false);
                    pauseVideos();
                  }}
                />
              </span>
            </div>

            <div className={styles.images}>
              <div
                className={styles.imagesContainer}
                style={{ transform: `translateX(-${translate}%)` }}
              >
                {safeGallery.map((media, i) => {
                  const img = getImage(media);

                  return (
                    <div className={styles.imageContainer} key={i}>
                      {img ? (
                        <Image
                          alt={img.altText || "Imperial Riding School"}
                          src={img.link}
                          width={img.mediaDetails?.width ?? 0}
                          height={img.mediaDetails?.height ?? 0}
                        />
                      ) : media?.video ? (
                        <Video video={media.video} isFullHeight />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={styles.texts}>
              <div
                className={styles.textsContainer}
                style={{ transform: `translateX(-${translate}%)` }}
              >
                {safeGallery.map((media, i) =>
                  media?.imageOrVideo?.[0] === "Image" ? (
                    <div
                      key={i}
                      dangerouslySetInnerHTML={{
                        __html: media?.image?.node?.description || "",
                      }}
                    />
                  ) : (
                    <div key={i}>
                      <p
                        dangerouslySetInnerHTML={{
                          __html: media?.videoDescription || "",
                        }}
                      />
                    </div>
                  ),
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
